import {CookingActivity, CookingActivityStep} from "./CookingActivity";
import {KitchenScreen} from "../../States/KitchenScreen";
import {array, Callback} from "../../Core/Reflection";
import {Sprite} from "../../Core/Sprite";
import {durianStep1, durianStep2, durianStep3, durianStep4, durianStep5, durianStep6} from "../../Layout/KitchenScreenLayout";
import {Container} from "../../Core/Container";
import {Ease} from "../../Core/Animatable";
import {Poly} from "../../Core/Shape";
import {CookingItem} from "./CookingItem";
import {playVO, ReactionVO} from "../../Data/VoiceOver";
import {BowlMix, CookingActivityStepMix} from "./CookingActivityStepMix";
import * as PIXI from "pixi.js";
import * as HLF from "html-living-framework";
import {Signal, Times} from "../../Core/Signal";
import {IPoint, PointUtil} from "../../Core/Geom";
import {AudioChannel} from "../../Core/AudioChannel";
import {Ingredient} from "./Ingredient";
import {Keyboard} from "../Keyboard";

export class DurianIceCreamActivity extends CookingActivity {

    constructor(kitchen: KitchenScreen) {
        super(kitchen, [
            DurianIceCreamStep1_Cut,
            DurianIceCreamStep2_Ingredients,
            DurianIceCreamStep3_Mix,
            DurianIceCreamStep4_Freeze,
            DurianIceCreamStep5_Toppings,
            DuranIceCreamStep6_Eat,
        ]);
    }
}

class DurianIceCreamStep1_Cut extends CookingActivityStep {

    readonly cuttingBoard = this.addChild(new Sprite());
    readonly durianCut = this.addChild(new (class extends CookingItem {
        readonly durianPieces = this.visuals.addChild(new Container());
        readonly durianEmpty = this.durianPieces.addChild(new Sprite());
        readonly durianFruit = this.durianPieces.addChild(new Sprite());
        readonly durianTop = this.durianPieces.addChild(new Sprite());
    })());

    play(onComplete: Callback, prev?: DuranIceCreamStep6_Eat) {
        durianStep1.call(this);

        if (prev) {
            this.moveItemsToRight(prev.bowlIceCream, () => {
                prev.destroy();
            });
        }

        this.durianCut.hoverVO = "LetsGoLuna_111";
        this.durianCut.enabled = false;

        this.moveItemsFromLeft(this.cuttingBoard, undefined, 0.3);
        const durian = this.durianCut;
        durian.draggable = false;
        this.moveItemsFromLeft(durian, () => {
            playVO("LetsGoLuna_107", () => {
                if (durian.enabled) {
                    durian.pulse(undefined, undefined, true);
                }
            });
            durian.enabled = true;
        }, 0.3);
        durian.durianPieces.visible = false;
        durian.onClick.add(() => {
            AudioChannel.SFX.play("knife-slice");
            Keyboard.remove(durian);
            durian.stopPulse();
            durian.enabled = false;
            durian.durianPieces.visible = true;
            durian.sprite.visible = false;
            const top = durian.durianTop;
            top.alphaTo(0, 0.2, {delay: 0.4});
            top.moveYTo(top.y - top.height, 0.6, Ease.sineIn, () => {
                durian.moveYTo(durian.y + this.cuttingBoard.height * 0.1, 0.05, Ease.sineIn, {delay: 0.1});
                this.moveItemsToRight(this.cuttingBoard, onComplete);
            });
        });
        Keyboard.add(durian);
    }
}

class DurianIceCreamStep2_Ingredients extends CookingActivityStep {

    readonly durianOpen = this.addChild(new (class extends Container {
        readonly durianEmpty = this.addChild(new Sprite());
        readonly durianFruit = this.addChild(new CookingItem());
    })());
    readonly container = this.addChild(new Container());
    readonly bowlMix = this.container.addChild(new BowlMix());
    readonly ingredientCream = this.container.addChild(new Ingredient());
    readonly ingredientSugar = this.container.addChild(new Ingredient());
    readonly ingredientVanilla = this.container.addChild(new Ingredient("#A5520D", 0.2));
    readonly arrow = this.container.addChild(new Sprite());
    readonly arrowEnd = {x: 0, y: 0};

    play(onComplete: Callback, prev: DurianIceCreamStep1_Cut): void {
        durianStep2.call(this);

        this.ingredientCream.hoverVO = "LetsGoLuna_108";
        this.ingredientSugar.hoverVO = "LetsGoLuna_109";
        this.ingredientVanilla.hoverVO = "LetsGoLuna_110";
        this.durianOpen.durianFruit.hoverVO = "LetsGoLuna_111";

        const bowl = this.bowlMix;
        const ingredients = [this.durianOpen.durianFruit, this.ingredientCream, this.ingredientSugar, this.ingredientVanilla];
        this.container.visible = false;
        ingredients.forEach(it => it.enabled = false);
        this.durianOpen.moveXYFrom(prev.durianCut.x, prev.durianCut.y, 0.6, Ease.backOut, () => {
            this.container.visible = true;
            bowl.bowlContent.visible = false;
            bowl.visible = false;
            this.arrow.visible = false;
            const onMoveIngredients = () => {
                bowl.visible = true;
                this.moveItemsFromRight(bowl, () => {
                    this.showArrow(this.arrow, this.arrowEnd);
                    bowl.enabled = true;
                    this.durianOpen.durianFruit.enabled = true;
                    this.durianOpen.durianFruit.changeParent(this.container);
                    Keyboard.focus(this.durianOpen.durianFruit);
                    let draggedIngredients = 0;
                    const onDragIngredient = () => {
                        this.hideArrow(this.arrow);
                        Keyboard.focus(ingredients.filter(it => it.enabled)[0] || this.kitchen.buttonHome);
                        const content = bowl.bowlContent;
                        if (!content.visible) {
                            content.visible = true;
                            content.tweenFrom(0.4, Ease.sineOut, {
                                alpha: 0,
                                scaleXY: content.scale.x * 0.9,
                                y: content.y + content.height * 0.2
                            });
                        }
                        draggedIngredients++;
                        if (draggedIngredients === ingredients.length) {
                            bowl.enabled = false;
                            this.moveItemsToLeft(this.durianOpen, onComplete);
                        }
                    };
                    for (const ingredient of ingredients) {
                        ingredient.target = bowl;
                        ingredient.targetTransform = {
                            x: ingredient === this.durianOpen.durianFruit ?
                                undefined : bowl.x - bowl.width * 0.2,
                            y: bowl.y - bowl.height * (
                                ingredient === this.durianOpen.durianFruit ? 0.2 :
                                    ingredient === this.ingredientCream ? 0.6 :
                                        ingredient === this.ingredientSugar ? 0.9 :
                                            ingredient === this.ingredientVanilla ? 0.6 :
                                                0.3),
                            rotation: ingredient === this.durianOpen.durianFruit ?
                                undefined : 80 * Math.PI / 180
                        };
                        ingredient.onKeyboard.add(() => {
                            Keyboard.focus(ingredients.filter(it => it.enabled)[0] || this.kitchen.buttonHome);
                        });
                        ingredient.onDragged.add(() => {
                            if (ingredient === this.durianOpen.durianFruit) {
                                ingredients.filter(it => it !== this.durianOpen.durianFruit).forEach(it => {
                                    it.enabled = true;
                                    it.pulse();
                                });
                            } else {
                                ingredients.forEach(it => it.stopPulse());
                            }
                            if (ingredient === this.ingredientCream) {
                                AudioChannel.SFX.play("liquid-pour");
                            }
                            ingredient.enabled = false;
                            if (ingredient instanceof Ingredient) {
                                ingredient.pour(onDragIngredient);
                            } else {
                                ingredient.alphaTo(0, 0.2, onDragIngredient);
                            }
                        });
                    }
                });
            };
            this.moveItemsFromLeft([this.ingredientVanilla, this.ingredientSugar, this.ingredientCream], onMoveIngredients);
        });
        prev.destroy();
        Keyboard.add(...ingredients);
    }
}

class DurianIceCreamStep3_Mix extends CookingActivityStepMix {

    play(onComplete: Callback, prev: DurianIceCreamStep2_Ingredients): void {
        durianStep3.call(this);

        const spoon = this.spoon;
        const bowl = this.bowlMix;
        spoon.visible = false;
        bowl.moveXYFrom(prev.bowlMix.x, prev.bowlMix.y, 0.6, Ease.sineInOut, () => {
            spoon.visible = true;
            spoon.enabled = false;
            this.moveItemsFromLeft(spoon, () => {
                const cb = Times(2, onComplete);
                this.kitchen.playFactVO(["LetsGoLuna_121", "LetsGoLuna_122"], 0, cb);
                this.mix(cb);
            });
        });
        prev.destroy();
    }
}

class DurianIceCreamStep4_Freeze extends CookingActivityStep {

    readonly bowlMix = this.addChild(new BowlMix());
    readonly bowlCook = this.addChild(new BowlIceCream());

    play(onComplete: Callback, prev: DurianIceCreamStep3_Mix): void {
        durianStep4.call(this);

        const bowlMix = this.bowlMix;
        const bowlCook = this.bowlCook;
        const prevBowl = prev.bowlMix;
        const freezer = this.kitchen.freezer;
        const luna = this.kitchen.luna;

        bowlMix.visible = false;
        bowlCook.visible = false;

        bowlMix.bowlContent.filters = prevBowl.bowlContent.filters;
        bowlCook.bowlContent.filters = prevBowl.bowlContent.filters;

        luna.show(() => {
            playVO(["LetsGoLuna_115"], () => {
                freezer.open();
                bowlMix.visible = true;
                bowlMix.tweenFrom(0.6, Ease.sineInOut, {
                    x: prevBowl.x,
                    y: prevBowl.y,
                    scaleX: prevBowl.scale.x,
                    scaleY: prevBowl.scale.y,
                    onComplete: () => {

                        const zIndex = this.kitchen.cookingActivity.zIndex;
                        this.kitchen.cookingActivity.zIndex = freezer.zIndex;

                        freezer.close(() => {
                            bowlMix.visible = false;

                            const timer = this.kitchen.freezerTimer;
                            timer.show(3, () => timer.hide(() => {
                                playVO("LetsGoLuna_173", () => {
                                    freezer.open(() => {
                                        this.kitchen.cookingActivity.zIndex = zIndex;
                                        bowlCook.visible = true;
                                        bowlCook.tweenFrom(0.6, Ease.sineInOut, {
                                            x: bowlMix.x,
                                            y: bowlMix.y,
                                            scaleX: bowlMix.scale.x,
                                            scaleY: bowlMix.scale.y,
                                            onComplete: () => freezer.close(() => luna.hide(onComplete))
                                        });
                                    });
                                });
                            }));
                        });
                    }
                });
                prev.destroy();
            });
        });
    }
}

class DurianIceCreamStep5_Toppings extends CookingActivityStep {

    readonly bowlIceCream = this.addChild(new BowlIceCream());
    readonly toppings = array(() => this.addChild(new ToppingBowl()));
    readonly toppingsDrag = array(() => new Topping());
    public topping = 0;

    play(onComplete: Callback, prev: DurianIceCreamStep4_Freeze): void {
        durianStep5.call(this);

        const bowl = this.bowlIceCream;
        bowl.enabled = true;

        const hoverVO = [
            "LetsGoLuna_112",
            "LetsGoLuna_113",
            "LetsGoLuna_114"
        ];

        let d = 0;
        for (let i = this.toppings.length - 1; i >= 0; i--) {
            const topping = this.toppings[i];
            const drag = this.toppingsDrag[i];
            topping.saveTransform();
            this.moveItemsFromLeft(topping, () => {
                topping.pulse();
                topping.enabled = true;
                if (topping === this.toppings[0]) {
                    playVO("LetsGoLuna_116");
                    Keyboard.focus(topping);
                }
            }, d += 0.2);
            topping.hoverVO = hoverVO[i];
            topping.enabled = false;
            topping.draggable = false;
            topping.target = bowl;
            topping.targetTransform = {
                y: bowl.y - bowl.height * 0.5,
                rotation: (topping === this.toppings[0] ? 1 : -1) * 80 * Math.PI / 180
            };
            const startDragTopping = (p: IPoint) => {
                this.toppings.forEach(it => it.stopPulse());
                this.addChild(drag);
                drag.startDrag(p);
            };
            topping.onStartDrag.add(e => {
                startDragTopping(e.data.getLocalPosition(this));
            });
            topping.onVirtualDrag.add(data => {
                const p = data.getLocalPosition(this);
                drag.x = p.x;
                drag.y = p.y;
            });
            topping.onDragged.add(() => {
                bowl.enabled = false;
                this.toppings.forEach(it => it.enabled = false);

                const s = 1000;
                const p = {x: bowl.x, y: bowl.y - bowl.height * 0.4};
                drag.moveXYTo(p.x, p.y, PointUtil.distance(drag, p) / s, Ease.sineInOut, () => {
                    const index = this.toppings.indexOf(topping);
                    this.topping = index;
                    bowl.eat(0);
                    bowl.setTopping(index);
                    drag.alphaTo(0, 0.2, () => {
                        drag.visible = false;
                        this.moveItemsToRight([...this.toppings].reverse(), onComplete);
                    });
                });
            });
            topping.onKeyboard.add(() => {
                startDragTopping(topping);
                Keyboard.focus(this.kitchen.buttonHome);
            });
            Keyboard.add(topping);
        }
        prev.destroy();
    }
}

class DuranIceCreamStep6_Eat extends CookingActivityStep {

    readonly bowlIceCream = this.addChild(new BowlIceCream());

    play(onComplete: Callback, prev: DurianIceCreamStep5_Toppings): void {
        durianStep6.call(this);

        const bowl = this.bowlIceCream;
        const eatingVO = [
            "LetsGoLuna_117",
            "LetsGoLuna_118",
            "LetsGoLuna_119",
            "LetsGoLuna_120"
        ];
        HLF.ArrayUtils.shuffle(eatingVO);
        playVO("LetsGoLuna_022");

        let index = 0;
        bowl.pulse();
        bowl.enabled = true;
        bowl.setTopping(prev.topping)
        bowl.onClick.add(() => {
            AudioChannel.SFX.play("chewing");
            bowl.stopPulse();
            index++;
            bowl.eat(index);
            if (index > 2) {
                bowl.enabled = false;
                const vo = eatingVO.pop();
                if (vo) {
                    playVO([...ReactionVO(), vo], onComplete);
                } else {
                    onComplete();
                }
            }
        });
        Keyboard.add(bowl);
        Keyboard.focus(bowl);
        prev.destroy();
    }
}

class BowlIceCream extends CookingItem {

    readonly bowlBack = this.visuals.addChild(new Sprite());
    readonly bowlFront = this.visuals.addChild(new Sprite());
    readonly bowlContent = this.visuals.addChild(new Container());
    readonly toppingsContent = this.visuals.addChild(new Container());
    readonly iceCream = array(() => this.bowlContent.addChild(new Sprite()));
    readonly toppings = array(() => this.toppingsContent.addChild(new Sprite()));
    readonly toppingMask: number[][] = [];

    constructor() {
        super();
        this.enabled = false;
        this.draggable = false;
    }

    onLayout() {
        this.iceCream.forEach((it, i) => it.visible = i === 0);
        this.toppings.forEach(it => it.visible = false);
    }

    setTopping(index: number) {
        this.toppings.forEach((topping, i) => {
            topping.visible = i === index;
            if (topping.visible && !topping.mask) {
                topping.mask = this.visuals.addChild(new Poly(this.toppingMask[0]));
            }
        });
    }

    eat(index: number) {
        this.iceCream.forEach((it, i) => it.visible = i === index);
        this.toppings.forEach(topping => {
            if (topping.visible) {
                if (topping.mask) {
                    (topping.mask as Poly).destroy();
                }
                const maskPoints = this.toppingMask[index];
                if (maskPoints) {
                    topping.mask = this.visuals.addChild(new Poly(maskPoints));
                } else {
                    topping.visible = false;
                }
            }
        });
    }
}

class ToppingBowl extends CookingItem {

    readonly onVirtualDrag = Signal<(data: PIXI.InteractionData) => void>();
}

class Topping extends Sprite {

    startDrag(p: IPoint) {
        this.x = p.x;
        this.y = p.y;
        this.alpha = 1;
        this.alphaFrom(0, 0.2);
    }
}